import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import {
	ChevronDownIcon,
	GlobeAltIcon,
	CheckIcon,
} from '@heroicons/react/24/outline'
import LogoLight from '../assets/images/logo_light.svg'
import MobileMenuOverlay from './MobileMenuOverlay'

const Header = () => {
	const { t, i18n } = useTranslation()
	const [showLanguageDropdown, setShowLanguageDropdown] = useState(false)
	const [isMenuOpen, setIsMenuOpen] = useState(false)
	const [isMenuClosing, setIsMenuClosing] = useState(false)

	// 컴포넌트 마운트 시 localStorage에서 언어 설정 확인
	useEffect(() => {
		const savedLanguage = localStorage.getItem('i18nextLng')
		console.log('Saved language:', savedLanguage)
		console.log('Current i18n language:', i18n.language)

		// localStorage에 저장된 언어가 있고, 현재 언어와 다르면 변경
		if (savedLanguage && savedLanguage !== i18n.language) {
			i18n.changeLanguage(savedLanguage)
		} else if (!savedLanguage) {
			// localStorage에 언어 설정이 없으면 한국어로 설정
			i18n.changeLanguage('ko')
			localStorage.setItem('i18nextLng', 'ko')
		}
	}, [i18n])

	// 현재 언어 정보
	const getCurrentLanguageName = () => {
		const currentLang = i18n.language
		console.log('Getting language name for:', currentLang)
		if (currentLang === 'ko' || currentLang === 'ko-KR') return '한국어'
		if (currentLang === 'jp' || currentLang === 'ja' || currentLang === 'ja-JP')
			return '日本語'
		return '한국어' // 기본값
	}

	const currentLanguage = getCurrentLanguageName()

	// 언어 변경 함수
	const handleLanguageChange = (languageCode: string) => {
		console.log('Changing language to:', languageCode)
		i18n.changeLanguage(languageCode)
		localStorage.setItem('i18nextLng', languageCode)
		sessionStorage.setItem('i18nextLng', languageCode)
		setShowLanguageDropdown(false)
	}

	// 언어 옵션들
	const languageOptions = [
		{ code: 'ko', name: '한국어' },
		{ code: 'ja', name: '日本語' },
	]

	// 드롭다운 참조
	const dropdownRef = useRef<HTMLDivElement>(null)

	// 외부 클릭 시 드롭다운 닫기
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setShowLanguageDropdown(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [])

	// 메뉴 닫기 함수 (애니메이션과 함께)
	const handleMenuClose = () => {
		setIsMenuClosing(true)
		setTimeout(() => {
			setIsMenuOpen(false)
			setIsMenuClosing(false)
		}, 600) // 애니메이션 지속시간과 동일
	}

	// 메뉴 토글 함수
	const handleMenuToggle = () => {
		if (isMenuOpen) {
			handleMenuClose()
		} else {
			setIsMenuOpen(true)
		}
	}

	// 메뉴 구조 정의
	const menuItems = [
		{
			name: t('header.project'),
			path: '/project',
			subItems: [
				{ name: t('header.aiMotionStudio'), path: '/project/motion-studio' },
				{ name: t('header.dopplemate'), path: '/project/dopplemate' },
				{ name: t('header.aiIntegrationLab'), path: '/project/integration-lab' },
			],
		},
		{
			name: t('header.about'),
			path: '/about',
			subItems: [{ name: t('header.about'), path: '/about' }],
		},
		{
			name: t('header.company'),
			path: '/company',
			subItems: [{ name: t('header.company'), path: '/company' }],
		},
		{
			name: t('header.contact'),
			path: '/contact',
			subItems: [{ name: t('header.contact'), path: '/contact' }],
		},
	]

	return (
		<>
			<header className="p-4 font-noto text-white fixed top-0 left-0 right-0 z-50">
				<div className="flex justify-between items-center">
					<Link to="/">
						<h1 className="text-2xl font-bold font-noto ml-2 md:ml-0">
							<img
								src={LogoLight}
								alt="MindCline"
								className="w-40 h-auto"
							/>
						</h1>
					</Link>
					{/* 1depth 메뉴 - 모바일에서 숨김 */}

					<div className="flex flex-col items-end">
						{/* 언어 선택 드롭다운 */}
						<div
							className="relative flex items-center"
							ref={dropdownRef}
						>
							<button
								onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
								className="bg-white text-blue-500 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors flex items-center space-x-2 w-[130px] me-4"
							>
								<GlobeAltIcon className="h-4 w-4" />
								<b>{currentLanguage}</b>
								<ChevronDownIcon
									className={`h-4 w-4 transition-transform duration-200 ${
										showLanguageDropdown ? 'rotate-180' : ''
									}`}
								/>
							</button>

							{/* 드롭다운 메뉴 */}
							{showLanguageDropdown && (
								<div className="absolute left-0 top-12 w-[140px] backdrop-blur-xl bg-white/70 rounded-xl shadow-2xl border border-white/20 py-2 z-50 overflow-hidden">
									<div className="absolute inset-0 bg-gradient-to-b from-white/50 to-transparent pointer-events-none" />
									{languageOptions.map((option, index) => {
										const isSelected =
											i18n.language === option.code ||
											(option.code === 'ko' && i18n.language === 'ko-KR') ||
											(option.code === 'ja' &&
												(i18n.language === 'ja' || i18n.language === 'ja-JP'))
										return (
											<button
												key={option.code}
												onClick={() => handleLanguageChange(option.code)}
												className={`relative w-full px-4 py-2.5 flex items-center justify-between transition-all duration-200 ${
													isSelected
														? 'bg-gradient-to-r from-blue-500/10 to-indigo-500/10 text-blue-600'
														: 'text-gray-600 hover:bg-gray-100/80 hover:text-gray-900'
												}`}
											>
												<span className="font-medium">{option.name}</span>
												{isSelected && <CheckIcon className="h-4 w-4 text-blue-500" />}
											</button>
										)
									})}
								</div>
							)}
							{/* 햄버거 메뉴 */}
							<div className="relative mr-3 md:mr-4">
								<button
									onClick={handleMenuToggle}
									className="relative w-8 h-8 flex flex-col justify-center items-center space-y-2 text-white hover:text-blue-400 focus:outline-none focus:text-blue-400 transition-colors duration-300"
									aria-label="메뉴 토글"
								>
									{/* 첫 번째 라인 */}
									<span className={`block h-0.5 w-10 bg-current`}></span>

									{/* 두 번째 라인 (가운데) */}
									<span className={`block h-0.5 w-10 bg-current`}></span>

									{/* 세 번째 라인 */}
									<span className={`block h-0.5 w-10 bg-current`}></span>
								</button>
							</div>
						</div>
					</div>
				</div>
			</header>
			{/* 모바일 메뉴 오버레이 */}
			<MobileMenuOverlay
				isMenuOpen={isMenuOpen}
				isMenuClosing={isMenuClosing}
				handleMenuClose={handleMenuClose}
				menuItems={menuItems}
			/>
		</>
	)
}

export default Header
